// Phase 7 — Track B T-B2. FormSubmit primitive.
//
// Submit button that auto-disables on `formState.isSubmitting` and shows
// an inline loader. Label defaults to children; while submitting, the
// label switches to `copy.shared.saving` per plan §2.8.
//
// Loader is `lucide-react`'s Loader2 icon with Tailwind's `animate-spin`,
// matching the shadcn convention. No new dep — `lucide-react` is already
// a Phase 6 dependency. The icon size aligns to the Button's text-line so
// the chrome height does not jitter when submission begins.

import * as React from 'react';
import { useFormContext } from 'react-hook-form';
import { Loader2 } from 'lucide-react';
import { Button, type ButtonProps } from './Button.js';
import { copy } from '@dailydeck/shared/copy';

export interface FormSubmitProps extends ButtonProps {
  /** Override the loading-state label. Defaults to `copy.shared.saving`. */
  loadingLabel?: string;
  children: React.ReactNode;
}

export const FormSubmit = React.forwardRef<HTMLButtonElement, FormSubmitProps>(
  (
    {
      children,
      loadingLabel = copy.shared.saving,
      disabled,
      variant = 'primary',
      type = 'submit',
      ...rest
    },
    ref,
  ) => {
    // useFormContext returns null outside <FormProvider>. We don't throw
    // here — FormSubmit is occasionally useful in standalone forms (e.g.
    // the existing useState-based login form during the migration). When
    // there's no context, the disabled-on-submitting behaviour is just a
    // no-op; the consumer can still pass `disabled={...}` manually.
    const ctx = useFormContext();
    const isSubmitting = ctx?.formState?.isSubmitting ?? false;

    return (
      <Button
        ref={ref}
        type={type}
        variant={variant}
        disabled={disabled ?? isSubmitting}
        aria-busy={isSubmitting ? true : undefined}
        {...rest}
      >
        {isSubmitting ? (
          <>
            <Loader2
              className="h-4 w-4 animate-spin"
              strokeWidth={1.75}
              aria-hidden="true"
            />
            <span>{loadingLabel}</span>
          </>
        ) : (
          children
        )}
      </Button>
    );
  },
);
FormSubmit.displayName = 'FormSubmit';
